'use client'

import type * as React from 'react'
import { cn } from '../lib/utils'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldItem,
  FieldLabel,
} from './field'
import { Fieldset, FieldsetLegend } from './fieldset'
import { Checkbox } from './checkbox'
import { CheckboxGroup } from './checkbox-group'
import { RadioGroup, Radio } from './radio-group'
import { Toggle, ToggleGroup } from './toggle-group'

type ChoiceFeedback = 'success' | 'error'

/** Shared inline choice anatomy, including editable rich labels in the block editor. */
function ChoiceChips({
  className,
  ...props
}: React.ComponentProps<typeof ToggleGroup>) {
  return (
    <ToggleGroup
      className={cn(
        'mx-1 inline-flex w-auto flex-wrap items-center gap-1 rounded-full align-middle',
        className,
      )}
      data-slot="choice-field-inline"
      render={<span />}
      {...props}
    />
  )
}

function ChoiceChip({
  multiple,
  feedback,
  invalid,
  className,
  ...props
}: React.ComponentProps<typeof Toggle> & {
  multiple?: boolean
  feedback?: ChoiceFeedback
  invalid?: boolean
}) {
  return (
    <Toggle
      className={cn(
        'border-border h-7 min-w-0 border-x-2 px-2.5 py-0 text-sm font-medium',
        multiple
          ? 'data-pressed:border-primary data-pressed:bg-primary data-pressed:text-on-primary gap-1.5 rounded-md'
          : 'data-pressed:border-primary data-pressed:bg-primary data-pressed:text-on-primary gap-1.5 rounded-full',
        feedback === 'success' &&
          'data-pressed:border-success data-pressed:bg-success data-pressed:text-on-success disabled:opacity-100',
        feedback === 'error' &&
          'data-pressed:border-error data-pressed:bg-error data-pressed:text-on-error disabled:opacity-100',
        invalid && 'border-error-border text-error',
        className,
      )}
      data-feedback={feedback}
      role={multiple ? 'checkbox' : 'radio'}
      {...props}
    />
  )
}

type ChoiceFieldBaseProps = {
  /** Identifies this field in native and Base UI form values. */
  name: string
  options: string[]
  /**
   * Selected option labels. ALWAYS an array, in BOTH single and multiple
   * mode (mirrors unni's UChoice so the quiz adapter ports 1:1). In single
   * mode it holds 0 or 1 entry.
   */
  value?: string[]
  onChange?: (value: string[]) => void
  /** Checkboxes (many selectable) instead of radios (one). */
  multiple?: boolean
  /** Compact chip controls that flow inside surrounding text. */
  inline?: boolean
  tip?: React.ReactNode
  /** Manual validity for the whole field. */
  state?: 'error' | 'success'
  /** Per-option validity — this is what quizzes use to mark correct/incorrect answers. Keyed by option label. */
  feedback?: Record<string, ChoiceFeedback>
  required?: boolean
  disabled?: boolean
  className?: string
}

type ChoiceFieldProps = ChoiceFieldBaseProps &
  (
    | { label: React.ReactNode; 'aria-label'?: never }
    | { label?: never; 'aria-label': string }
  )

function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function validateRequiredGroup(value: unknown) {
  return Array.isArray(value) && value.length > 0 ? null : 'Required!'
}

function displayedFeedback(
  feedback: ChoiceFeedback | undefined,
  selected: boolean,
): ChoiceFeedback | undefined {
  return feedback === 'error' && !selected ? undefined : feedback
}

function ChoiceField({
  name,
  label,
  'aria-label': ariaLabel,
  options,
  value,
  onChange,
  multiple,
  inline,
  tip,
  state,
  feedback,
  required,
  disabled,
  className,
}: ChoiceFieldProps) {
  // unni renders every option in the error color and swaps the tip for
  // 'Required!' when a required choice has nothing selected yet.
  const missingRequired = required && !value?.length
  const resolvedTip = missingRequired ? 'Required!' : tip
  const resolvedState = missingRequired ? 'error' : state

  if (inline) {
    const selectedOptions = value ?? []

    return (
      <ChoiceChips
        aria-label={
          ariaLabel ?? (typeof label === 'string' ? label : undefined)
        }
        className={className}
        disabled={disabled}
        multiple={multiple}
        onValueChange={(nextValue) => {
          onChange?.(nextValue)
        }}
        value={selectedOptions}
      >
        {options.map((option) => {
          const selected = selectedOptions.includes(option)
          const optionFeedback = displayedFeedback(feedback?.[option], selected)
          return (
            <ChoiceChip
              aria-checked={selected}
              feedback={optionFeedback}
              invalid={missingRequired}
              data-testid={capitalizeFirstLetter(option)}
              disabled={disabled}
              key={option}
              multiple={multiple}
              value={option}
            >
              {capitalizeFirstLetter(option)}
            </ChoiceChip>
          )
        })}
        {!disabled &&
          selectedOptions.map((option) => (
            <input key={option} name={name} type="hidden" value={option} />
          ))}
      </ChoiceChips>
    )
  }

  const optionClassName = (optionFeedback: ChoiceFeedback | undefined) => {
    return cn(
      'flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-base/normal font-normal transition-colors select-none sm:text-base/normal',
      disabled
        ? 'cursor-not-allowed select-none'
        : 'hover:bg-surface-2 cursor-pointer',
      feedback !== undefined &&
        !optionFeedback &&
        !missingRequired &&
        'opacity-50',
      optionFeedback === 'error' || missingRequired
        ? 'text-error'
        : optionFeedback === 'success' && 'text-success',
    )
  }

  const group = multiple ? (
    <CheckboxGroup
      aria-label={ariaLabel}
      value={value}
      onValueChange={(nextValue) => onChange?.(nextValue)}
      disabled={disabled}
    />
  ) : (
    <RadioGroup
      aria-label={ariaLabel}
      value={value?.[0] ?? null}
      onValueChange={(nextValue) =>
        onChange?.(nextValue === null ? [] : [nextValue as string])
      }
      disabled={disabled}
      required={required}
    />
  )

  return (
    <Field
      name={name}
      disabled={disabled}
      invalid={resolvedState === 'error'}
      validate={required && multiple ? validateRequiredGroup : undefined}
      data-state={resolvedState}
      className={cn('gap-2', className)}
    >
      <Fieldset render={group} disabled={disabled} className="max-w-none gap-3">
        {label && (
          <FieldsetLegend className="text-on-surface text-label-big mb-0 inline-flex items-center gap-2 font-medium">
            {label}
            {required && <span className="text-error">*</span>}
          </FieldsetLegend>
        )}
        <div className="flex flex-col gap-0">
          {options.map((option) => {
            const selected = value?.includes(option) ?? false
            const optionFeedback = displayedFeedback(
              feedback?.[option],
              selected,
            )
            return (
              <FieldItem key={option} className="w-full">
                <FieldLabel className={optionClassName(optionFeedback)}>
                  {multiple ? (
                    <Checkbox
                      value={option}
                      data-choice-field-ignore-invalid={
                        !missingRequired || undefined
                      }
                      data-feedback={optionFeedback}
                      className="mt-0.5 shrink-0"
                    />
                  ) : (
                    <Radio
                      value={option}
                      data-choice-field-ignore-invalid={
                        !missingRequired || undefined
                      }
                      data-feedback={optionFeedback}
                      className="mt-0.5 shrink-0"
                    />
                  )}
                  {capitalizeFirstLetter(option)}
                </FieldLabel>
              </FieldItem>
            )
          })}
        </div>
      </Fieldset>
      {resolvedState === 'error' ? (
        <FieldError className="text-label-big select-none" match>
          {resolvedTip}
        </FieldError>
      ) : (
        <>
          {/* form-driven mode: renders only when constraint validation fails */}
          <FieldError />
          {resolvedTip && (
            <FieldDescription
              className={
                resolvedState === 'success'
                  ? 'text-label-big text-success select-none'
                  : 'text-label-big select-none'
              }
            >
              {resolvedTip}
            </FieldDescription>
          )}
        </>
      )}
    </Field>
  )
}

export { ChoiceField, ChoiceChips, ChoiceChip, type ChoiceFieldProps }
